import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE = '/api/crm';
const DEFAULT_TIMEOUT = 30_000;
const MAX_RETRIES = 2;
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

interface RetryConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
}

function shouldRetry(error: AxiosError, config: RetryConfig): boolean {
  const method = config.method?.toUpperCase() || '';
  if (!RETRYABLE_METHODS.has(method)) return false;
  const retries = config.__retryCount || 0;
  if (retries >= MAX_RETRIES) return false;
  if (!error.response) return true;
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504 || status === 429;
}

function backoffMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 5000);
}

function addAuthInterceptors(instance: ReturnType<typeof axios.create>) {
  instance.defaults.timeout = instance.defaults.timeout || DEFAULT_TIMEOUT;

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('grant_crm_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryConfig | undefined;
      if (config && shouldRetry(error, config)) {
        const nextAttempt = (config.__retryCount || 0) + 1;
        config.__retryCount = nextAttempt;
        await new Promise((r) => setTimeout(r, backoffMs(nextAttempt - 1)));
        return instance.request(config);
      }

      if (error.response?.status === 401) {
        localStorage.removeItem('grant_crm_token');
        localStorage.removeItem('grant_crm_user');
        window.location.href = '/login';
        return new Promise(() => {});
      }

      // Report 5xx and network errors to backend monitoring
      const status = error.response?.status;
      if ((status && status >= 500) || !error.response) {
        const url = error.config?.url || '';
        const method = error.config?.method?.toUpperCase() || '';
        fetch('/api/error-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: (status && status >= 500) ? 'api_5xx' : 'network_error',
            message: `${method} ${url} → ${status || 'NO_RESPONSE'}: ${error.message}`,
            source: 'axios',
            url: location.href,
            userAgent: navigator.userAgent,
          }),
        }).catch(() => {});
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

// CRM-specific instance (baseURL: /api/crm)
export const api = addAuthInterceptors(
  axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } })
);

// General auth instance for non-CRM endpoints (dashboard, meetings, admin, sms, knowledge-base)
export const authAxios = addAuthInterceptors(
  axios.create({ headers: { 'Content-Type': 'application/json' } })
);

export { API_BASE };
